import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '../lib/supabase'

interface Member {
  id: string
  email: string
  role: string
}
interface Invite {
  id: string
  email: string
  role: string
  expires_at: string
}
interface TeamData {
  you: string | null
  youAreAdmin: boolean
  members: Member[]
  invites: Invite[]
  seatsUsed: number
  seatLimit: number
}

function expiresIn(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400_000)
  return days <= 0 ? 'expired' : `expires in ${days}d`
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] ${role === 'admin' ? 'bg-signal/15 text-signal' : 'bg-surface-1 text-[var(--text-muted)]'}`}
    >
      {role === 'admin' ? 'Admin' : 'Member'}
    </span>
  )
}

/**
 * Team / seats — shared ecosystem flow (an invitee joins the org and sees whatever it holds, across
 * SchedulesMCP / LoadingMCP / TrackingMCP). Admin-only invites, capped by the org's seat allowance.
 */
export function TeamPanel() {
  const [data, setData] = useState<TeamData | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'admin'>('member')
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    const r = await apiFetch('/team')
    if (r.ok) setData(r.json.data as TeamData)
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  async function invite(e: FormEvent) {
    e.preventDefault()
    if (!email.trim() || busy) return
    setBusy('invite')
    setErr(null)
    const r = await apiFetch('/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), role }),
    })
    setBusy(null)
    if (r.ok) {
      setEmail('')
      void load()
    } else {
      setErr(r.json?.error?.message ?? 'Could not send the invite.')
    }
  }
  async function act(key: string, path: string, method: string) {
    setBusy(key)
    setErr(null)
    const r = await apiFetch(path, { method })
    setBusy(null)
    if (!r.ok && r.json?.error?.message) setErr(r.json.error.message)
    void load()
  }

  if (!data) return null
  const full = data.seatsUsed >= data.seatLimit
  const inputCls =
    'rounded-lg border border-border bg-surface-1 px-3.5 py-2 text-sm focus:border-signal focus:outline-none'

  return (
    <section className="mb-6 rounded-2xl border border-border bg-surface-0 p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-bold font-display text-lg">Team</h2>
          <p className="mt-0.5 text-[var(--text-muted)] text-sm">
            Teammates share your watches & saved work. One login across SchedulesMCP, LoadingMCP &
            TrackingMCP.
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-border px-3 py-1 font-mono text-[var(--text-muted)] text-xs">
          {data.seatsUsed} / {data.seatLimit} seat{data.seatLimit === 1 ? '' : 's'}
        </span>
      </div>

      {err && <p className="mb-3 text-sm text-warn">{err}</p>}

      {data.youAreAdmin &&
        (full ? (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-1 p-4 text-sm">
            <span className="text-[var(--text-muted)]">
              All {data.seatLimit} seat{data.seatLimit === 1 ? '' : 's'} in use. A team plan
              includes 5.
            </span>
            <Link
              to="/pricing"
              className="rounded-lg bg-signal px-3.5 py-1.5 font-bold text-black text-xs"
            >
              Add seats
            </Link>
          </div>
        ) : (
          <form onSubmit={invite} className="mb-4 flex flex-wrap items-center gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com"
              className={`min-w-0 flex-1 ${inputCls}`}
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
              className={inputCls}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={busy === 'invite'}
              className="rounded-lg bg-signal px-4 py-2 font-bold text-black text-sm disabled:opacity-50"
            >
              {busy === 'invite' ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        ))}

      <div className="space-y-2">
        {data.members.map((m) => (
          <div
            key={m.id}
            className="flex items-center justify-between gap-3 border-border/50 border-b pb-2 text-sm last:border-0"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate">{m.email}</span>
              {m.email === data.you && (
                <span className="text-[var(--text-muted)] text-xs">you</span>
              )}
              <RoleBadge role={m.role} />
            </div>
            {data.youAreAdmin && m.email !== data.you && (
              <button
                type="button"
                onClick={() => act(`rm-${m.id}`, `/team/member/${m.id}`, 'DELETE')}
                disabled={busy === `rm-${m.id}`}
                className="shrink-0 text-[var(--text-muted)] text-xs transition hover:text-warn disabled:opacity-50"
              >
                {busy === `rm-${m.id}` ? '…' : 'Remove'}
              </button>
            )}
          </div>
        ))}
        {data.invites.map((iv) => (
          <div
            key={iv.id}
            className="flex items-center justify-between gap-3 border-border/50 border-b pb-2 text-sm last:border-0"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[var(--text-muted)]">{iv.email}</span>
              <span className="text-[var(--text-muted)] text-xs">
                pending · {expiresIn(iv.expires_at)}
              </span>
              <RoleBadge role={iv.role} />
            </div>
            {data.youAreAdmin && (
              <div className="flex shrink-0 gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => act(`re-${iv.id}`, `/team/invite/${iv.id}/resend`, 'POST')}
                  disabled={busy === `re-${iv.id}`}
                  className="text-[var(--text-muted)] transition hover:text-[var(--text-primary)] disabled:opacity-50"
                >
                  {busy === `re-${iv.id}` ? '…' : 'Resend'}
                </button>
                <button
                  type="button"
                  onClick={() => act(`rv-${iv.id}`, `/team/invite/${iv.id}`, 'DELETE')}
                  disabled={busy === `rv-${iv.id}`}
                  className="text-[var(--text-muted)] transition hover:text-warn disabled:opacity-50"
                >
                  {busy === `rv-${iv.id}` ? '…' : 'Revoke'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
